/* What next round does to your food: income in, upkeep out, and the difference.

   Requirement 18d found a game where food fell 5560 → 0 and starvation took 541 troops in one tick,
   and fixed the half of it that was a standing production order nobody could see. The other road to
   the same famine is an army whose upkeep has outgrown its farms, and the HUD still showed only a
   falling number — by the time it is obviously falling, the tick that empties it has already been
   ordered.

   `apply_troops_consumption` takes losses proportionally, so at zero food the whole army starves at
   once. There is no partial round of warning, which is exactly why the warning has to come before.

   Mirrors `collect_from_cultivation_center`, `collect_from_putrefactor` and
   `apply_troops_consumption`, including their `int()` truncation at each step — a prediction that
   rounds differently from the server is a prediction that disagrees with it. Constants come from
   `GET /game-config/`. */

import { consumptionMultiplier, productionMultiplier } from "src/utils/gameModifiers"
import { isUnderSiege } from "src/utils/siege"
import { scale } from "src/utils/ladder"

const cellsOwnedBy = (cells, userId) =>
  Object.values(cells || {}).filter((cell) => String(cell.owner) === String(userId))

/* What one cell's farm actually harvests a round — the *real* number, workers and all, exactly as
   `collect_from_cultivation_center` / `collect_from_putrefactor` compute it. A human farm scales
   its output by the workers standing in it (a half-staffed farm yields half), which is the whole
   reason the potential and the reality can differ; the horde's rot needs no staffing. Shared so a
   food number on any screen is the same claim the server will honour, matching the server's
   int()-at-each-step truncation. */
export const cellFoodProduction = ({ gameConfig, team, race, researchLevels, level, workers }) => {
  if (!level) return 0
  const multiplier = productionMultiplier({ gameConfig, team, race, researchLevels })
  // Snapped to steps of 50, exactly as the server does since Q9a (owner, 2026-09-03: values
  // move in steps of 50 or 100 — "114/round is terrible. It should be either 100 or 150").
  if (team === "human") {
    const settings = gameConfig?.human_config?.BUILDINGS?.cultivation_center
    if (!settings || !workers) return 0
    const production = Math.trunc(settings.PRODUCTION_PER_LEVEL * scale(level) * multiplier)
    const required = settings.REQUIRED_WORKERS_PER_LEVEL * scale(level)
    const harvest = Math.trunc(production * Math.min(workers / required, 1))
    return 50 * Math.round(harvest / 50)
  }
  const settings = gameConfig?.zombie_config?.BUILDINGS?.putrefactor
  if (!settings) return 0
  const rotted = Math.trunc(settings.PRODUCTION_PER_LEVEL * scale(level) * multiplier)
  return 50 * Math.round(rotted / 50)
}

export const foodIncomeFor = ({ gameConfig, cells, userId, team, race, researchLevels, users, movements }) =>
  foodIncomeBreakdown({ gameConfig, cells, userId, team, race, researchLevels, users, movements }).reduce(
    (total, entry) => total + entry.actual,
    0
  )

/* The raw pre-multiplier bill, split garrison / in transit, per unit type. The road eats too
   (owner's Q13a, mirrored from `apply_troops_consumption`): a movement's stack pays upkeep like
   a garrisoned one, and skipping it here was exactly the "consumption looks wrong" the owner
   reported. */
const upkeepCounts = ({ gameConfig, cells, movements, userId, team }) => {
  const troops = gameConfig?.[team === "human" ? "human_config" : "zombie_config"]?.TROOPS
  if (!troops) return { garrison: {}, transit: {} }

  const tally = (bucket, units) => {
    Object.entries(units || {}).forEach(([unitType, data]) => {
      const consumption = troops[unitType]?.CONSUMPTION || 0
      // Human workers are population: they eat nothing and can never starve.
      if (consumption <= 0) return
      const number = data?.number || 0
      if (number > 0) bucket[unitType] = (bucket[unitType] || 0) + number
    })
  }

  const garrison = {}
  Object.values(cells || {}).forEach((cell) => tally(garrison, cell.metadata_by_user?.[userId]?.units))
  const transit = {}
  ;(movements || [])
    .filter((movement) => movement.user_id === userId)
    .forEach((movement) => tally(transit, movement.units))
  return { garrison, transit, troops }
}

export const foodUpkeepFor = ({ gameConfig, cells, movements, userId, team, race }) => {
  const { garrison, transit, troops } = upkeepCounts({ gameConfig, cells, movements, userId, team })
  if (!troops) return 0
  const total = [garrison, transit].reduce(
    (sum, bucket) =>
      sum +
      Object.entries(bucket).reduce(
        (bucketSum, [unitType, number]) => bucketSum + (troops[unitType]?.CONSUMPTION || 0) * number,
        0
      ),
    0
  )

  // Applied to the bill rather than per unit, as the server does — rounding per unit would quietly
  // lose the Warrior's discount on small stacks.
  return Math.trunc(total * consumptionMultiplier({ gameConfig, race }))
}

/* The bill, itemised (owner's Q13b): who eats what, garrison and road apart, so the net number on
   the HUD can be audited line by line. Lines are pre-multiplier — the discount is applied to the
   whole bill (see foodUpkeepFor), so it is shown as its own line rather than smeared per unit. */
export const foodUpkeepBreakdown = ({ gameConfig, cells, movements, userId, team }) => {
  const { garrison, transit, troops } = upkeepCounts({ gameConfig, cells, movements, userId, team })
  if (!troops) return []
  const lines = (bucket, inTransit) =>
    Object.entries(bucket)
      .map(([unitType, number]) => ({
        unitType,
        number,
        upkeep: (troops[unitType]?.CONSUMPTION || 0) * number,
        inTransit,
      }))
      .sort((a, b) => b.upkeep - a.upkeep)
  return [...lines(garrison, false), ...lines(transit, true)]
}

/* Each city's harvest, itemised (owner's Q13b): what it actually yields against what its farm
   could yield fully staffed — the gap is workers missing, and the row says so as a percentage. */
/* Per city: what it will actually bring in next round, and what it would bring in fully staffed.

   `besieged` is the third state, and it is the one that makes `actual` 0 whatever the staffing
   (owner, 2026-09-08): the three `collect_*` steps skip a cell with a fight on it, so a stormed
   farm harvests nothing. `users` and `movements` are what decide that — without them the siege
   cannot be seen and every farm reads as at peace, which is the old behaviour and the safe
   fallback for callers that have not been given them. */
export const foodIncomeBreakdown = ({ gameConfig, cells, userId, team, race, researchLevels, users, movements }) => {
  const foodBuilding = team === "human" ? "cultivation_center" : "putrefactor"
  return Object.entries(cells || {})
    .filter(([, cell]) => String(cell.owner) === String(userId))
    .map(([key, cell]) => {
      const mine = cell.metadata_by_user?.[userId]
      const level = mine?.buildings?.[foodBuilding]?.level || 0
      const workers = mine?.units?.worker?.number || 0
      const besieged = users ? isUnderSiege({ coords: key, cells, users, movements }) : false
      const harvest = cellFoodProduction({ gameConfig, team, race, researchLevels, level, workers })
      const required =
        (gameConfig?.human_config?.BUILDINGS?.cultivation_center?.REQUIRED_WORKERS_PER_LEVEL || 0) * scale(level)
      const potential =
        team === "human"
          ? cellFoodProduction({ gameConfig, team, race, researchLevels, level, workers: required })
          : harvest
      return { key, level, workers, required, actual: besieged ? 0 : harvest, potential, besieged }
    })
    .filter((entry) => entry.level > 0)
    .sort((a, b) => b.actual - a.actual)
}

export const foodOutlookFor = (args) => {
  const income = foodIncomeFor(args)
  const upkeep = foodUpkeepFor(args)
  return { income, upkeep, net: income - upkeep }
}

export default foodOutlookFor

/* How well the fields are staffed, across every farm this player owns (owner's S11): the workers
   standing in them against the workers they need at their level. The gap is the whole reason a
   human's income can fall short of its rating, and it reads best as one bar rather than a
   paragraph — `share` is the percentage staffed, `missing` the workers the fields still want.
   Nothing to say for the horde, whose rot needs no hands: `required` is 0 there. */
export const foodStaffingFor = ({ gameConfig, cells, userId, team }) => {
  if (team !== "human") return { staffed: 0, required: 0, missing: 0, share: 100 }
  const perLevel = gameConfig?.human_config?.BUILDINGS?.cultivation_center?.REQUIRED_WORKERS_PER_LEVEL || 0
  const totals = cellsOwnedBy(cells, userId).reduce(
    (sum, cell) => {
      const mine = cell.metadata_by_user?.[userId]
      const level = mine?.buildings?.cultivation_center?.level || 0
      if (!level) return sum
      const required = perLevel * scale(level)
      const workers = mine?.units?.worker?.number || 0
      return { staffed: sum.staffed + Math.min(workers, required), required: sum.required + required }
    },
    { staffed: 0, required: 0 }
  )
  const missing = Math.max(0, totals.required - totals.staffed)
  const share = totals.required > 0 ? Math.round((totals.staffed / totals.required) * 100) : 100
  return { ...totals, missing, share }
}
